import React, { useState, useRef, useEffect } from 'react';
import { Camera, Trash2, Download, Undo, Redo, Maximize } from 'lucide-react';

// 主组件
const ImageSanctifier = () => {
  // 状态管理
  const [image, setImage] = useState(null);
  const [brush, setBrush] = useState(30);
  const [opacity, setOpacity] = useState(80);
  const [glow, setGlow] = useState(20);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  
  // 引用
  const canvasRef = useRef(null);
  const imageCanvasRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  
  // 图片加载处理
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        resetHistory();
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };
  
  // 触发文件选择
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  // 处理历史记录
  const resetHistory = () => {
    setHistory([]);
    setRedoStack([]);
  };
  
  const saveToHistory = (imageData) => {
    setHistory(prev => [...prev, imageData]);
    setRedoStack([]);
  };
  
  const undo = () => {
    if (history.length === 0) return;
    
    const drawCanvas = drawCanvasRef.current;
    const ctx = drawCanvas.getContext('2d');
    
    const lastAction = history[history.length - 1];
    setRedoStack(prev => [...prev, lastAction]);
    
    setHistory(prev => prev.slice(0, -1));
    
    // 清除绘制画布
    ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
    
    // 重新绘制历史操作
    history.slice(0, -1).forEach(action => {
      ctx.putImageData(action, 0, 0);
    });
  };
  
  const redo = () => {
    if (redoStack.length === 0) return;
    
    const drawCanvas = drawCanvasRef.current;
    const ctx = drawCanvas.getContext('2d');
    
    const action = redoStack[redoStack.length - 1];
    setHistory(prev => [...prev, action]);
    setRedoStack(prev => prev.slice(0, -1));
    
    ctx.putImageData(action, 0, 0);
  };
  
  // 绘图功能
  const startDrawing = (e) => {
    if (!image) return;
    setIsDrawing(true);
    draw(e);
  };
  
  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    const drawCanvas = drawCanvasRef.current;
    const ctx = drawCanvas.getContext('2d');
    saveToHistory(ctx.getImageData(0, 0, drawCanvas.width, drawCanvas.height));
  };
  
  const draw = (e) => {
    if (!isDrawing || !image) return;
    
    const drawCanvas = drawCanvasRef.current;
    const ctx = drawCanvas.getContext('2d');
    
    const rect = drawCanvas.getBoundingClientRect();
    let x, y;
    
    if (e.touches) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    // 创建光效
    const alpha = opacity / 100;
    const blurSize = glow;
    
    ctx.save();
    
    // 添加模糊效果
    ctx.filter = `blur(${blurSize}px)`;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(x, y, brush / 2, 0, Math.PI * 2);
    ctx.fill();
    
    // 添加中心更亮的部分
    ctx.filter = 'none';
    ctx.globalAlpha = alpha + 0.2;
    ctx.beginPath();
    ctx.arc(x, y, brush / 4, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  };
  
  // 全屏处理
  const toggleFullscreen = () => {
    setFullscreen(!fullscreen);
  };
  
  // 清除所有
  const clearAll = () => {
    if (!image) return;
    
    const drawCanvas = drawCanvasRef.current;
    const ctx = drawCanvas.getContext('2d');
    
    const imageData = ctx.getImageData(0, 0, drawCanvas.width, drawCanvas.height);
    saveToHistory(imageData);
    
    ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
  };
  
  // 导出处理后的图片 - 修复后的函数
  const exportImage = () => {
    if (!image) return;
    
    try {
      const canvas = canvasRef.current;
      const imageCanvas = imageCanvasRef.current;
      const drawCanvas = drawCanvasRef.current;
      
      // 确保画布尺寸正确
      const width = imageCanvas.width;
      const height = imageCanvas.height;
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      // 清除画布
      ctx.clearRect(0, 0, width, height);
      // 绘制原始图片
      ctx.drawImage(imageCanvas, 0, 0);
      // 绘制圣光效果
      ctx.drawImage(drawCanvas, 0, 0);
      
      // 转换为数据URL
      const dataUrl = canvas.toDataURL('image/png');
      
      // 创建下载链接并触发点击
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = '圣光处理图片.png';
      document.body.appendChild(link); // 添加到DOM中确保在所有浏览器中都能正常工作
      link.click();
      document.body.removeChild(link); // 清理DOM
    } catch (error) {
      console.error('导出图片时出错:', error);
      alert('导出图片时出现错误，请重试');
    }
  };
  
  // 当图片改变时，重置画布
  useEffect(() => {
    if (!image) return;
    
    const canvas = canvasRef.current;
    const imageCanvas = imageCanvasRef.current;
    const drawCanvas = drawCanvasRef.current;
    
    // 设置画布尺寸
    const containerWidth = containerRef.current.clientWidth;
    const scale = Math.min(1, containerWidth / image.width);
    
    const width = image.width * scale;
    const height = image.height * scale;
    
    canvas.width = width;
    canvas.height = height;
    imageCanvas.width = width;
    imageCanvas.height = height;
    drawCanvas.width = width;
    drawCanvas.height = height;
    
    // 绘制原始图片
    const imgCtx = imageCanvas.getContext('2d');
    imgCtx.clearRect(0, 0, width, height);
    imgCtx.drawImage(image, 0, 0, width, height);
    
    // 清除绘制层
    const drawCtx = drawCanvas.getContext('2d');
    drawCtx.clearRect(0, 0, width, height);
  }, [image]);
  
  return (
    <div className={`flex flex-col items-center p-4 bg-gray-100 rounded-lg ${fullscreen ? 'fixed inset-0 z-50' : ''}`}>
      <h2 className="text-2xl font-bold mb-4 text-center">图片圣光处理工具</h2>
      
      <div className="w-full max-w-3xl mb-4">
        <div 
          ref={containerRef} 
          className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-200 flex justify-center"
          style={{ minHeight: image ? 'auto' : '300px' }}
        >
          {!image && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
              <Camera size={48} />
              <p className="mt-2">请上传一张图片开始编辑</p>
            </div>
          )}
          
          <canvas ref={canvasRef} className="hidden"></canvas>
          <canvas ref={imageCanvasRef} className={image ? 'block' : 'hidden'}></canvas>
          <canvas 
            ref={drawCanvasRef}
            className={image ? 'absolute top-0 left-0 z-10' : 'hidden'}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          ></canvas>
        </div>
      </div>
      
      <div className="w-full max-w-3xl grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="flex flex-col space-y-4 p-4 bg-white rounded-lg shadow">
          <h3 className="font-bold text-lg">上传图片</h3>
          <input 
            ref={fileInputRef}
            type="file" 
            accept="image/*" 
            onChange={handleImageUpload}
            className="hidden"
          />
          <button 
            onClick={triggerFileInput}
            className="flex items-center justify-center space-x-2 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition"
          >
            <Camera size={20} />
            <span>选择图片</span>
          </button>
        </div>
        
        <div className="flex flex-col space-y-4 p-4 bg-white rounded-lg shadow">
          <h3 className="font-bold text-lg">圣光效果设置</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              圣光大小: {brush}px
            </label>
            <input 
              type="range" 
              min="5" 
              max="100" 
              value={brush} 
              onChange={(e) => setBrush(parseInt(e.target.value))}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              不透明度: {opacity}%
            </label>
            <input 
              type="range" 
              min="10" 
              max="100" 
              value={opacity} 
              onChange={(e) => setOpacity(parseInt(e.target.value))}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              模糊程度: {glow}px
            </label>
            <input 
              type="range" 
              min="0" 
              max="50" 
              value={glow} 
              onChange={(e) => setGlow(parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
      </div>
      
      <div className="w-full max-w-3xl flex flex-wrap justify-center gap-2 mb-4">
        <button 
          onClick={undo} 
          disabled={history.length === 0}
          className={`flex items-center space-x-1 py-2 px-4 rounded-lg ${history.length === 0 ? 'bg-gray-300 text-gray-500' : 'bg-gray-200 hover:bg-gray-300 transition'}`}
        >
          <Undo size={16} />
          <span>撤销</span>
        </button>
        
        <button 
          onClick={redo} 
          disabled={redoStack.length === 0}
          className={`flex items-center space-x-1 py-2 px-4 rounded-lg ${redoStack.length === 0 ? 'bg-gray-300 text-gray-500' : 'bg-gray-200 hover:bg-gray-300 transition'}`}
        >
          <Redo size={16} />
          <span>重做</span>
        </button>
        
        <button 
          onClick={clearAll} 
          disabled={!image}
          className={`flex items-center space-x-1 py-2 px-4 rounded-lg ${!image ? 'bg-gray-300 text-gray-500' : 'bg-red-100 text-red-600 hover:bg-red-200 transition'}`}
        >
          <Trash2 size={16} />
          <span>清除所有</span>
        </button>
        
        <button 
          onClick={toggleFullscreen}
          disabled={!image} 
          className={`flex items-center space-x-1 py-2 px-4 rounded-lg ${!image ? 'bg-gray-300 text-gray-500' : 'bg-gray-200 hover:bg-gray-300 transition'}`}
        >
          <Maximize size={16} />
          <span>{fullscreen ? '退出全屏' : '全屏编辑'}</span>
        </button>
        
        <button 
          onClick={exportImage} 
          disabled={!image}
          className={`flex items-center space-x-1 py-2 px-4 rounded-lg ${!image ? 'bg-gray-300 text-gray-500' : 'bg-green-500 text-white hover:bg-green-600 transition'}`}
        >
          <Download size={16} />
          <span>导出图片</span>
        </button>
      </div>
      
      <div className="w-full max-w-3xl">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="font-bold text-lg mb-2">使用说明</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
            <li>上传图片后，在需要添加圣光的区域上按住鼠标拖动</li>
            <li>可以调整圣光的大小、不透明度和模糊程度</li>
            <li>支持撤销和重做操作</li>
            <li>完成后可导出图片</li>
            <li>支持移动设备触摸操作</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ImageSanctifier;